import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Send, Loader2, Upload, FileCheck2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", role: "", message: "" };

const inputCls =
  "w-full bg-cream border border-line rounded-xl px-4 py-3 text-sm md:text-base text-ink placeholder:text-inksoft/50 focus:outline-none focus:ring-2 focus:ring-terra/40 focus:border-terra transition-[box-shadow,border-color] duration-200";

export const Careers = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext) || f.size > 5 * 1024 * 1024) {
      toast.error(t.careers.cvInvalid);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error(t.careers.cvInvalid);
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("consent", consent ? "true" : "false");
      fd.append("cv", file);
      const res = await fetch(`${API}/careers`, { method: "POST", body: fd });
      if (!res.ok) throw new Error();
      toast.success(t.careers.success);
      setForm(EMPTY);
      setFile(null);
      setConsent(false);
    } catch {
      toast.error(t.careers.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="lavora-con-noi" data-testid="careers-section" className="py-24 lg:py-32 bg-creamdark border-t border-line">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="text-xs md:text-sm font-semibold uppercase tracking-[0.22em] text-terra mb-6"
          >
            {t.careers.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-serif font-medium tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            {t.careers.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 max-w-md text-base md:text-lg leading-relaxed text-inksoft"
          >
            {t.careers.sub}
          </motion.p>
        </div>

        <motion.form
          data-testid="careers-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 bg-white border border-line rounded-3xl p-7 md:p-10 shadow-[0_20px_60px_rgba(45,50,43,0.07)]"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="careers-name" className="block text-xs font-semibold uppercase tracking-widest text-inksoft mb-2">{t.contact.nameLabel}</label>
              <input id="careers-name" data-testid="careers-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={t.contact.namePlaceholder} className={inputCls} />
            </div>
            <div>
              <label htmlFor="careers-email" className="block text-xs font-semibold uppercase tracking-widest text-inksoft mb-2">{t.contact.emailLabel}</label>
              <input id="careers-email" data-testid="careers-email-input" type="email" required value={form.email} onChange={set("email")} placeholder={t.contact.emailPlaceholder} className={inputCls} />
            </div>
            <div>
              <label htmlFor="careers-phone" className="block text-xs font-semibold uppercase tracking-widest text-inksoft mb-2">{t.contact.phoneLabel}</label>
              <input id="careers-phone" data-testid="careers-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder={t.contact.phonePlaceholder} className={inputCls} />
            </div>
            <div>
              <label htmlFor="careers-role" className="block text-xs font-semibold uppercase tracking-widest text-inksoft mb-2">{t.careers.roleLabel}</label>
              <select id="careers-role" data-testid="careers-role-select" required value={form.role} onChange={set("role")} className={inputCls}>
                <option value="" disabled>{t.careers.rolePlaceholder}</option>
                {t.careers.roles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-5">
            <label htmlFor="careers-message" className="block text-xs font-semibold uppercase tracking-widest text-inksoft mb-2">{t.contact.messageLabel}</label>
            <textarea id="careers-message" data-testid="careers-message-input" rows={4} value={form.message} onChange={set("message")} placeholder={t.contact.messagePlaceholder} className={`${inputCls} resize-y`} />
          </div>
          <div className="mt-5">
            <span className="block text-xs font-semibold uppercase tracking-widest text-inksoft mb-2">{t.careers.cvLabel}</span>
            <label
              data-testid="careers-cv-label"
              className="flex items-center justify-center gap-3 border-2 border-dashed border-line hover:border-terra rounded-2xl px-6 py-7 cursor-pointer transition-colors duration-200 bg-cream"
            >
              <input type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="hidden" data-testid="careers-cv-input" />
              {file ? <FileCheck2 size={20} strokeWidth={1.5} className="text-terra" /> : <Upload size={20} strokeWidth={1.5} className="text-terra" />}
              <span className="text-sm font-medium text-ink">
                {file ? `${t.careers.cvSelected} ${file.name}` : t.careers.cvButton}
              </span>
            </label>
          </div>
          <label className="mt-5 flex items-start gap-3 cursor-pointer" data-testid="careers-consent-label">
            <input
              type="checkbox"
              required
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 shrink-0 accent-[#C86A51]"
              data-testid="careers-consent-checkbox"
            />
            <span className="text-sm leading-relaxed text-inksoft">
              {t.contact.consentText}{" "}
              <Link to="/privacy" data-testid="careers-privacy-link" className="text-terra font-medium underline underline-offset-2">Privacy Policy</Link>
            </span>
          </label>
          <button
            data-testid="careers-submit-button"
            type="submit"
            disabled={sending}
            className="mt-7 inline-flex items-center gap-2 bg-terra hover:bg-terradark disabled:opacity-60 text-cream text-sm font-semibold px-8 py-3.5 rounded-full transition-colors duration-300"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={2} />}
            {sending ? t.careers.sending : t.careers.submit}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
